import type { FormDialogRef } from '@/components'
import {
  Button,
  FAIcon,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  Textarea,
} from '@/components'
import { cn } from '@/lib/utils'
import { useFormWatch } from '@/utils'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useRef } from 'react'
import { isMobile } from 'react-device-detect'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import {
  CREATE_SALARY_COMMENT_MUTATION,
  PATCH_SALARY_COMMENT_MUTATION,
} from '../mutations'
import type { SalaryCommentNode } from '../types.graphql'

function useSalaryCommentSchema() {
  const translations = useTranslate({
    commentRequired: 'validation.field-required',
  })

  return z.object({
    id: z.string().optional(),
    user: z.string().min(1, translations.commentRequired),
    value: z.string().min(1, translations.commentRequired),
  })
}

export type SalaryCommentForm = z.infer<
  ReturnType<typeof useSalaryCommentSchema>
>

type SalaryComment = SalaryCommentForm & { id: string }

export interface SalaryCommentDialogRef {
  open(salaryComment: SalaryComment): void
}

function useInitialFilters(
  userId: string,
  comment?: SalaryCommentNode
): SalaryCommentForm {
  return {
    user: userId,
    value: comment?.value || '',
    id: comment?.id,
  }
}

interface Props {
  comment?: SalaryCommentNode
  userId: string
  year: number
  month: number

  hideTrigger?: boolean

  onSubmit?(): void
}

export function SalaryCommentDialog({
  comment,
  userId,
  year,
  month,
  hideTrigger = false,
  onSubmit,
}: Props) {
  const translations = useTranslate({
    labels: {
      comment: 'common.comment',
    },
    headerAdd: 'salary.salary-comment-dialog.header-add',
    headerEdit: 'salary.salary-comment-dialog.header-edit',
    add: 'common.add',
  })

  const dialogRef = useRef<FormDialogRef>(null)

  const form = useForm<SalaryCommentForm>({
    resolver: zodResolver(useSalaryCommentSchema()),
    defaultValues: useInitialFilters(userId, comment),
  })

  const commentValue = useFormWatch(form, 'value')

  const salaryCommentId = useFormWatch(form, 'id')
  const [createSalaryComment] = useMutation(CREATE_SALARY_COMMENT_MUTATION, {
    refetchQueries: [],
    onCompleted() {
      dialogRef.current?.close()
    },
  })

  const [patchSalaryComment] = useMutation(PATCH_SALARY_COMMENT_MUTATION, {
    refetchQueries: [],
    onCompleted() {
      dialogRef.current?.close()
    },
  })

  const confirmDisabled = !commentValue?.trim()

  function resetForm() {
    setTimeout(() => {
      form.reset()
    }, 300)
  }

  async function handleSubmit(values: SalaryCommentForm) {
    if (!values.id) {
      await createSalaryComment({
        variables: {
          input: {
            user: values.user,
            year,
            month,
            value: values.value,
          },
        },
      })
    } else {
      await patchSalaryComment({
        variables: {
          id: values.id,
          input: {
            value: values.value,
          },
        },
      })
    }
    onSubmit?.()
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      trigger={
        <Button className={cn(hideTrigger && 'hidden')} size="sm">
          <FAIcon icon={comment ? 'edit' : 'plus'} />
          {!comment ? <span>{translations.add}</span> : null}
        </Button>
      }
      header={
        !salaryCommentId ? translations.headerAdd : translations.headerEdit
      }
      noCloseOnEscape
      confirmDisabled={confirmDisabled}
      error={undefined}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Controller
          name="value"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.comment}</FieldLabel>
              <Textarea {...field} rows={isMobile ? 6 : 4} />
            </Field>
          )}
        />
      </FieldGroup>
    </FormDialog>
  )
}
